import { useTranslation } from 'next-i18next';

import { valueof } from '@/types';
import { useAppDispatch, useAppSelector } from '@/redux';
import { selectCurrentStepLabel, selectPreviouslyValidatedSteps } from '@/redux/slices/checkout.slice';

import { redirectToAnotherStep } from '@/redux/thunks/checkout/redirectToAnotherStep';

import Icon from '@/components/common/iconedLinks/Icon';

import styles from './StepProgressSection.module.scss';
import { STEPS } from '@/constants/checkout';
import { URLS } from '@/constants/urls';

export const StepProgressSection = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const includedSteps = ['address', 'shipping', 'review', 'pay'] as const;
  const currentStep = useAppSelector(selectCurrentStepLabel);
  const previouslyValidatedSteps = useAppSelector(selectPreviouslyValidatedSteps);

  const progressLabel = (step: valueof<typeof STEPS>) => {
    const keyForCurrentStep = {
      address: 'shippingAddress',
      billing: 'billingAddress',
      shipping: 'shipMode',
      review: 'cartReview',
      pay: 'payMode'
    };
    const key = keyForCurrentStep[step];
    return t(`checkout:${key}.progress`);
  };

  const handleStepRedirection = (step: typeof includedSteps[number]) => () => {
    if (step === currentStep) {
      return;
    }
    if (!previouslyValidatedSteps.includes(step)) {
      return;
    }

    dispatch(redirectToAnotherStep(step));
  };

  const isStepValidated = (step: typeof includedSteps[number]) => {
    return previouslyValidatedSteps.includes(step);
  };

  return (
    <div className={styles['steps-container']}>
      <section className={styles.steps}>
        {includedSteps.map((step, index) => {
          const active = step === currentStep;
          const notDone = !previouslyValidatedSteps.includes(step);
          const done = previouslyValidatedSteps.includes(step);

          const classNames = [
            active ? styles.active : '',
            notDone && !active ? styles['future-step'] : '',
            done ? styles['previous-step'] : ''
          ].join(' ');

          return (
            <section
              key={step} className={classNames}
              onClick={handleStepRedirection(step)}
            >
              {isStepValidated(step) ? (
                <Icon src={URLS.CHECKED_CIRCLE_GREEN} alt={t('alter:checkMarkIcon')} width={28} height={28} />
              ) : (
                <p className={styles['step-index']}>{index + 1}</p>
              )}
              <p className={styles['step-label']}>{progressLabel(step)}</p>
            </section>
          );
        })}
      </section>
    </div>
  );
};
